<template>
  <ModalOverlay
    hide-header
    :model-value="openModal"
    :no-close-backdrop="true"
    :background-class="backgroundClass"
    :hide-close-btn="configuration.isLoginPage"
    @close="closeModal">
    <template #body>
      <div
        v-if="specialDiscountData"
        class="b-pop-up--special-discount__holder">
        <PopUpLoginHeader @close="closeModal"></PopUpLoginHeader>

        <img
          v-if="specialDiscountData.imageUrl.url"
          class="b-pop-up--special-discount__img"
          :src="specialDiscountData.imageUrl.url"
          :alt="specialDiscountData.imageUrl.alternateText" />
        <div
          v-if="specialDiscountData.title"
          class="b-pop-up--special-discount__title"
          v-html="specialDiscountData.title"></div>
        <div
          v-if="specialDiscountData.text"
          class="b-pop-up--special-discount__body"
          v-html="specialDiscountData.text"></div>
        <div
          v-if="dictionary.specialDiscountFooter"
          class="b-pop-up--special-discount__footer"
          v-html="dictionary.specialDiscountFooter"></div>
      </div>

      <PopUpLoginHeader
        v-if="contestInfo"
        class="d-block d-md-none"
        :hide-button="hideCloseButton()"
        @close="closeModal"></PopUpLoginHeader>

      <div v-if="contestInfo" class="b-pop-up--contest__container">
        <div
          v-if="contestInfo.title"
          class="b-pop-up--contest__container__title"
          v-html="contestInfo.title"></div>
        <img
          v-if="contestInfo.image.url"
          class="b-pop-up--contest__container__image"
          :src="contestInfo.image.url"
          :alt="contestInfo.image.alt" />
        <div
          v-if="contestInfo.duration"
          class="b-pop-up--contest__container__duration">
          <i class="icon-calendar"></i>
          <span v-html="contestInfo.duration"></span>
        </div>
        <div
          v-if="dictionary.contestFooter"
          class="b-pop-up--contest__container__footer"
          v-html="dictionary.contestFooter"></div>
      </div>

      <PopUpLoginHeader
        v-if="onlyMembersInfo"
        class="d-block d-md-none"
        :hide-button="hideCloseButton()"
        @close="closeModal"></PopUpLoginHeader>

      <div v-if="onlyMembersInfo" class="b-pop-up--only-members__container">
        <img
          class="b-pop-up--only-members__container__image"
          :src="onlyMembersInfo.image.url"
          :alt="onlyMembersInfo.image.alt" />

        <div class="b-pop-up--only-members__container__info">
          <div class="b-pop-up--only-members__container__title">
            {{ onlyMemebersTitle }}
          </div>

          <div class="b-pop-up--only-members__container__footer">
            {{ dictionary.onlyMembersFooter }}
          </div>
        </div>
      </div>

      <div class="b-pop-up--login__main">
        <Loader v-if="isLoading" />

        <PopUpLoginHeader
          :class="{ 'd-none d-md-block': contestInfo || onlyMembersInfo }"
          :hide-button="hideCloseButton()"
          @close="closeModal"></PopUpLoginHeader>

        <div class="b-pop-up__content-container">
          <div class="b-pop-up__social-content">
            <Alert
              v-if="errorMessages.length"
              :messages="errorMessages"
              :dismissible="false" />

            <template v-if="isWebsite">
              <div class="b-pop-up__mini-title">
                {{ dictionary.loginWith }}
              </div>
              <div class="b-pop-up__social-holder">
                <FacebookLogin
                  class="b-pop-up__social-holder__item"
                  :button-text="dictionary.facebook"
                  @loading="(load) => (isLoading = load)"
                  @close-modal="closeModal"
                  @show-modal-pending-registration="
                    showPendingRegistrationModal = true
                  "
                  @show-alert="showAlert"
                  @hide-alert="hideAlert"></FacebookLogin>
                <GoogleLogin
                  class="b-pop-up__social-holder__item"
                  :button-text="dictionary.google"
                  @loading="(load) => (isLoading = load)"
                  @close-modal="closeModal"
                  @show-modal-pending-registration="
                    showPendingRegistrationModal = true
                  "
                  @show-alert="showAlert"
                  @hide-alert="hideAlert"></GoogleLogin>
              </div>
            </template>
          </div>

          <div class="b-pop-up__input-area">
            <template v-if="isWebsite">
              <div class="b-pop-up__mini-title">
                {{ dictionary.byEmail }}
              </div>
              <Form v-slot="{ meta, errors }" @submit="onSubmitForm">
                <div class="b-pop-up__input-holder">
                  <div class="form-floating">
                    <Field
                      class="form-control"
                      :class="{
                        'form-control--error':
                          errors.email && errors.email.length,
                      }"
                      name="email"
                      type="email"
                      autocomplete="username"
                      :placeholder="dictionary.emailPlaceholder"
                      :validate-on-input="emailMeta.touched"
                      rules="required|email"
                      @blur="emailBlur" />
                    <label for="email">{{ dictionary.emailPlaceholder }}</label>
                    <ErrorMessage v-slot="{ message }" name="email">
                      <div class="field-validation-error">
                        {{ message }}
                      </div>
                    </ErrorMessage>
                  </div>
                  <div class="form-floating">
                    <Field
                      class="form-control"
                      :class="{
                        'form-control--error':
                          errors.password && errors.password.length,
                      }"
                      name="password"
                      autocomplete="current-password"
                      :type="passwordInputType"
                      :placeholder="dictionary.passwordPlaceholder"
                      :validate-on-input="passwordMeta.touched"
                      rules="required"
                      @blur="passwordBlur" />
                    <label for="password">
                      {{ dictionary.passwordPlaceholder }}
                    </label>
                    <button
                      class="form-floating__btn-password"
                      :class="
                        showPassword ? 'btn--password-closed' : 'btn--password'
                      "
                      type="button"
                      title="toggle show/hide"
                      @click="() => (showPassword = !showPassword)"></button>
                    <ErrorMessage v-slot="{ message }" name="password">
                      <div class="field-validation-error">
                        {{ message }}
                      </div>
                    </ErrorMessage>
                  </div>
                  <div class="b-pop-up__form-elements">
                    <div
                      v-if="ctaForgot && ctaForgot.url"
                      class="b-pop-up__forgot-password">
                      <Link
                        id="forgotPasswordButton"
                        :url="ctaForgot.url"
                        :target="ctaForgot.target"
                        :title="ctaForgot.title" />
                    </div>
                    <div class="custom-chck custom-chck--right-side">
                      <!--e-simple-checkbox--right-side-->
                      <Field
                        id="rememberMe"
                        v-model="rememberMe"
                        name="rememberMe"
                        type="checkbox"
                        :value="rememberMe"
                        :unchecked-value="false" />
                      <label for="rememberMe">
                        <span class="custom-chck__span">
                          {{ dictionary.rememberMe }}
                        </span>
                      </label>
                    </div>
                  </div>
                  <div class="b-pop-up__cta-area">
                    <button
                      type="submit"
                      class="b-pop-up__cta-login btn--block"
                      :disabled="isLoading || !meta.valid">
                      <span>{{ dictionary.login }}</span>
                    </button>
                    <div v-if="ctaRegister && ctaRegister.url" class="btn-dual">
                      <div class="btn-dual__text">
                        {{ dictionary.notInVillage }}
                      </div>

                      <Link
                        class="btn-dual__lnk"
                        :url="ctaRegister.url"
                        :target="ctaRegister.target"
                        :title="ctaRegister.title" />
                    </div>
                    <button
                      v-if="isInBookingFlow && !onlyMembersInfo"
                      type="button"
                      class="b-pop-up__cta-login btn--block"
                      @click="closeModal">
                      <span>{{ dictionary.continueAsGuest }}</span>
                    </button>

                    <button
                      v-if="onlyMembersInfo"
                      type="button"
                      class="b-pop-up__cta-cancel"
                      @click="goToHome">
                      <span>{{ dictionary.onlyMembersLoginCancel }}</span>
                    </button>
                  </div>
                </div>
              </Form>
            </template>
            <template v-else>
              <div class="b-pop-up__input-holder">
                <div class="b-pop-up__cta-area">
                  <button
                    type="button"
                    class="b-pop-up__cta-login btn--block"
                    :disabled="isLoading"
                    @click="onClickMobileLogin">
                    <span>{{ dictionary.login }}</span>
                  </button>
                  <div v-if="ctaRegister && ctaRegister.url" class="btn-dual">
                    <div class="btn-dual__text">
                      {{ dictionary.notInVillage }}
                    </div>

                    <button
                      type="button"
                      class="btn-dual__lnk"
                      :disabled="isLoading"
                      @click="onClickMobileRegister">
                      <span>{{ ctaRegister.title }}</span>
                    </button>
                  </div>
                  <button
                    v-if="isInBookingFlow && !onlyMembersInfo"
                    type="button"
                    class="b-pop-up__cta-login btn--block"
                    @click="closeModal">
                    <span>{{ dictionary.continueAsGuest }}</span>
                  </button>

                  <button
                    v-if="onlyMembersInfo"
                    type="button"
                    class="b-pop-up__cta-cancel"
                    @click="goToPreSales">
                    <span>{{ dictionary.onlyMembersLoginCancel }}</span>
                  </button>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>
  </ModalOverlay>

  <ModalOverlay
    v-model="showPendingRegistrationModal"
    background-class="b-pop-up--alert-success b-pop-up__holder"
    header-background-class="b-pop-up__header">
    <template #header>
      <div class="b-pop-up__header__title">
        {{ dictionary.pendingRegistrationHeaderTitle }}
      </div>
    </template>
    <template #body>
      <div class="b-pop-up__alert-success__content">
        <div
          class="b-pop-up__alert-success__icon b-pop-up__alert-success__icon--success"></div>
        <div class="b-pop-up__alert-success__content__container">
          <div class="b-pop-up__alert-success__title">
            {{ dictionary.pendingRegistrationBodyTitle }}
          </div>
          <div class="b-pop-up__alert-success__text">
            {{ dictionary.pendingRegistrationBodyText }}
          </div>
          <button
            type="button"
            class="b-pop-up__alert-success__cta"
            @click="showPendingRegistrationModal = false">
            {{ dictionary.pendingRegistrationButtonCloseText }}
          </button>
        </div>
      </div>
    </template>
  </ModalOverlay>

  <ModalOverlay
    v-model="showUpdateAccountModal"
    :header-background-class="
      desktop ? 'b-pop-up__header' : 'b-pop-up__title-holder'
    "
    background-class="b-pop-up--alert-success b-pop-up__holder"
    no-close-backdrop>
    <template #header>
      <div class="b-pop-up__header__title">
        {{ dictionary.updateAccountHeader }}
      </div>
    </template>
    <template #body>
      <div class="b-pop-up__content">
        <div class="b-pop-up__alert-success__text">
          {{ dictionary.updateAccountDescription }}
        </div>
        <button
          class="p-booking__modal__confirm__cta btn-primary-orange"
          type="button"
          @click="onClickUpdateAccountOK">
          <span>{{ dictionary.updateAccountButtonOKText }}</span>
        </button>
        <button
          type="button"
          class="p-booking__modal__confirm__cta btn-secondary"
          @click="onClickUpdateAccountCancel">
          <span>{{ dictionary.updateAccountButtonCancelText }}</span>
        </button>
      </div>
    </template>
  </ModalOverlay>
</template>

<script setup>
import useViewport from '../../features/viewport'
import { computed, onMounted, ref } from 'vue'
import ModalOverlay from '../components/ModalOverlay.vue'
import {
  checkCaptcha,
  getData,
  getLocale,
  getUrlParam,
  updateUserDisplay,
} from '../../lib/lib'
import { getFormattedDateWithSeparators } from '../../lib/date'
import {
  ErrorMessage,
  Field,
  Form,
  configure,
  defineRule,
  useField,
  useForm,
} from 'vee-validate'
import { email, required } from '@vee-validate/rules'
import { localize } from '@vee-validate/i18n'
import Loader from '../components/Shared/Loader.vue'
import Alert from '../components/Shared/Alert.vue'
import FacebookLogin from '../components/Shared/FacebookLogin.vue'
import GoogleLogin from '../components/Shared/GoogleLogin.vue'
import Link from '../components/UI/Link.vue'
import PopUpLoginHeader from '../components/UI/PopUpLoginHeader.vue'
import postRequestHandler from '../../lib/postRequestHandler'
import getErrorMessage from '../../lib/getErrorMessage'
import constants from '../../lib/constants'

defineRule('required', required)
defineRule('email', email)
const { dictionary, configuration } = getData('loginModalData')
const { ctaRegister, ctaForgot, loginHandler, refresh, preSalesUrlPath } =
  configuration
const { desktop } = useViewport()

const rememberMe = ref(true)
const openModal = ref(false)
const showPassword = ref(false)
const isLoading = ref(true)
const errorMessages = ref([])
const showPendingRegistrationModal = ref(false)
const isInBookingFlow = window['bookingData'] || window['shoppingCafeData']
const showUpdateAccountModal = ref(false)
const updateAccountRedirectUrl = ref(null)
const accountData = ref(null)

const { resetForm } = useForm()

const { handleBlur: emailBlur, meta: emailMeta } = useField(
  'email',
  'required|email',
)
const { handleBlur: passwordBlur, meta: passwordMeta } = useField(
  'password',
  'required',
)

onMounted(() => {
  checkCaptcha(() => (isLoading.value = false))

  configure({
    generateMessage: localize(getLocale(), {
      fields: {
        email: {
          required: dictionary.emailRequired,
          email: dictionary.emailRegex,
        },
        password: {
          required: dictionary.passwordRequired,
        },
      },
    }),
  })

  jQuery(document).on('login-clicked', (e, data) => {
    if (data) {
      const { action } = data
      if (action === 'popupWithLogin') {
        updateSpecialDiscountData(data)
        openModal.value = true
        return
      }
      if (action === 'contestLogin') {
        updateContestInfo(data.contestInfo)
        openModal.value = true
        return
      }
      if (action === 'onlyMembersLogin') {
        updateOnlyMembersInfo(data.onlyMembersInfo)
        openModal.value = true
        return
      }
    }

    updateSpecialDiscountData(null)
    updateContestInfo(null)
    updateOnlyMembersInfo(null)

    if (!jQuery('.header').hasClass('header--loggedin')) {
      openModal.value = true
      return
    }
  })

  if (getUrlParam('forceLogin') || configuration.isLoginPage) {
    jQuery(document).trigger('login-clicked')
  }
})

const closeModal = () => {
  if (contestInfo.value) {
    if (accountData.value) {
      window.location.reload()
      return
    }
    window.location.href = '/'
    return
  }

  if (!configuration.isLoginPage) {
    resetForm()
    openModal.value = false
  }
}

const goToHome = () => {
  window.location.href = '/'
}

const goToPreSales = () => {
  window.location.href = preSalesUrlPath
}

const onClickMobileLogin = () => {
  if (typeof window.MobileApp !== 'undefined') {
    window.MobileApp.navigate('login')
  }
}

const onClickMobileRegister = () => {
  if (window.MobileApp !== undefined) {
    window.MobileApp.navigate('register')
  }
}

const isWebsite = computed(() => {
  const { deviceType } = configuration
  return deviceType === constants.deviceType.website
})

const passwordInputType = computed(() => {
  return !showPassword.value ? 'password' : 'text'
})

const backgroundClass = computed(() => {
  const baseClasses = 'b-pop-up--login b-pop-up__holder'
  const specialDiscountClass = specialDiscountData.value
    ? 'b-pop-up--special-discount'
    : ''
  const contestClass = contestInfo.value ? 'b-pop-up--contest' : ''
  const onlyMembersClass = onlyMembersInfo.value ? 'b-pop-up--only-members' : ''
  return `${baseClasses} ${specialDiscountClass} ${onlyMembersClass} ${contestClass}`.trim()
})

const onlyMemebersTitle = computed(() => {
  return dictionary.onlyMembersAvailablelDateUntil.replace(
    '{0}',
    onlyMembersDateFormatted.value,
  )
})

const onlyMembersDateFormatted = computed(() => {
  return getFormattedDateWithSeparators(
    onlyMembersInfo.value.date,
    'en-GB',
    { year: 'numeric', month: '2-digit', day: '2-digit' },
    '.',
  )
})

const onSubmitForm = async (values) => {
  if (isLoading.value) {
    return false
  }

  isLoading.value = true
  errorMessages.value = []

  const postData = {
    email: values.email,
    password: values.password,
    rememberMe: values.rememberMe,
  }

  await postRequestHandler({
    postData,
    endpoint: loginHandler,
    successCallback: handleSuccess,
    errorCallback: handleError,
    refreshTokenHandler: refresh,
    captchaAction: configuration.captchaAction,
  })
}

const handleError = (messages) => {
  isLoading.value = false

  errorMessages.value = getErrorMessage(
    messages,
    dictionary.defaultErrorMessage,
  )
}

const handleSuccess = (data) => {
  isLoading.value = false

  accountData.value = data.data.accountData

  const {
    firstName,
    fullName,
    lastName,
    avatar,
    updateAccount,
    updateAccountUrl,
  } = accountData.value

  updateUserDisplay({
    firstName,
    fullName,
    lastName,
    img: avatar.url,
  })

  if (accountData.value) {
    document.dispatchEvent(
      new CustomEvent('login-was-held', {
        detail: { accountData: accountData.value },
      }),
    )

    if (updateAccount) {
      showUpdateAccountModal.value = true
      updateAccountRedirectUrl.value = updateAccountUrl
      console.log(updateAccountRedirectUrl.value, updateAccountUrl)
    } else {
      window.location.reload()
    }
  }

  closeModal()
}

// specialDiscountData
const specialDiscountData = ref(null)

const updateSpecialDiscountData = (data) => {
  if (data) {
    specialDiscountData.value = { ...data }
  } else {
    specialDiscountData.value = null
  }
}

// contestInfo
const contestInfo = ref(null)

const updateContestInfo = (data) => {
  if (data) {
    contestInfo.value = { ...data }
  } else {
    contestInfo.value = null
  }
}

// onlyMembersInfo
const onlyMembersInfo = ref(null)

const updateOnlyMembersInfo = (data) => {
  if (data) {
    onlyMembersInfo.value = { ...data }
  } else {
    onlyMembersInfo.value = null
  }
}

const showAlert = (messages) => {
  errorMessages.value = messages
}

const hideAlert = () => {
  errorMessages.value = []
}

const hideCloseButton = () => {
  if (onlyMembersInfo.value) {
    return true
  }
  return configuration.isLoginPage
}

const onClickUpdateAccountOK = () => {
  const { value } = updateAccountRedirectUrl
  if (value) {
    window.location.assign(value)
  }
}

const onClickUpdateAccountCancel = () => {
  showUpdateAccountModal.value = false
}
</script>
